import {i18n} from '@yourcoach/i18n';

export interface Currency {
  id: string;
  sign: string;
  toString: () => string;
}

interface CurrencyEnum {
  EUR: Currency;
  GBP: Currency;
  USD: Currency;
}

export const Currency: CurrencyEnum = {
  EUR: {
    id: 'eur',
    sign: '€',
  },
  GBP: {
    id: 'gbp',
    sign: '£',
  },
  USD: {
    id: 'usd',
    sign: '$',
  },
};

Object.keys(Currency).forEach(key => {
  const currency: Currency = Currency[key];

  currency.toString = () =>
    /**
     * @i18n_parser
     * t('shared:currency.usd')
     * t('shared:currency.eur')
     * t('shared:currency.gbp')
     */
    i18n.t(`shared:currency.${currency.id}`);
});

export const getPriceString = ({
  currency = Currency.USD,
  intZeroPrice = false,
  price = 0,
  simpleFormat = false,
}: {
  currency?: Currency;
  intZeroPrice?: boolean;
  price: number;
  simpleFormat?: boolean;
}): string => {
  if (price === 0) {
    return intZeroPrice ? `${currency.sign}0` : i18n.t('shared:payment.free');
  }

  if (simpleFormat) {
    return i18n.t('shared:payment.paid');
  }

  const normalizedPrice = price / 100;

  const isWithoutFractionalPart = normalizedPrice % 1 === 0;

  const priceString = normalizedPrice.toLocaleString(
    // takes a device locale
    undefined,
    {
      minimumFractionDigits: isWithoutFractionalPart ? 0 : 2,
    },
  );

  return `${currency.sign}${priceString}`;
};

/**
 * Stripe commission: 30 cents + 2.9%
 */
export const extractCommission = (
  price = 0,
  config = {
    fixed: 30,
    percent: 2.9,
  },
) => Math.round(price * (config.percent / 100)) + 30;

// it can be useful for PriceTextInput component
export const parseAmountString = (value: string) => {
  const shouldSetEmpty = value === '' || value === '.' || value === ',';
  const isHasDecimalPoint = value.includes('.');
  const isHasDecimalComma = value.includes(',');
  const isHasDecimalSeparator = isHasDecimalPoint || isHasDecimalComma;

  if (shouldSetEmpty) {
    value = '';
  } else if (isHasDecimalSeparator) {
    let separator = '.';

    if (isHasDecimalComma) {
      separator = ',';
    }

    const [num, fraction] = value.split(separator);

    value = `${num}${separator}${fraction ? fraction.slice(0, 2) : ''}`;
  }

  return value;
};
